import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {MODALITY, Modality} from '@domain/model/Account/Subscription/Subscription';
import {OptionSelector} from '@components/molecules/OptionSelector';

import {TEXT} from './FrequencySelector.definition';
import {useMonthlyDefaultSubscriptionExperiment} from '@views/Order/PlaceOrder/_hooks/useMonthlyDefaultSubscription';
import {useOverallInfo} from '@components/_context';

interface FrequencySelectorProps {
  readonly modality: Modality;
  readonly onModalityChange: (modality: Modality) => void;
  readonly hideDescriptionOnBlur?: boolean;
  readonly hideDescription?: boolean;
  readonly hiddenOptions?: Modality[];
}

const FrequencySelector: React.FC<FrequencySelectorProps> = ({
  modality,
  onModalityChange,
  hideDescriptionOnBlur = false,
  hideDescription = false,
  hiddenOptions = [],
}) => {
  const {formatMessage} = useIntl();
  const {hasAnyOrders} = useOverallInfo();
  const {control, notAssigned, v1} = useMonthlyDefaultSubscriptionExperiment({isFirstOrder: !hasAnyOrders});

  const options = useMemo(
    () => [
      {
        id: MODALITY.MONTHLY,
        title: formatMessage(TEXT.MODALITY_MONTHLY_TITLE),
        content: formatMessage(TEXT.MODALITY_MONTHLY_DESC),
        label: v1 ? formatMessage(TEXT.MOST_POPULAR) : undefined,
      },
      {
        id: MODALITY.BIMONTHLY,
        title: formatMessage(TEXT.MODALITY_BIMONTHLY_TITLE),
        content: formatMessage(TEXT.MODALITY_BIMONTHLY_DESC),
        label: control || notAssigned ? formatMessage(TEXT.MOST_POPULAR) : undefined,
      },
      {
        id: MODALITY.QUARTERLY,
        title: formatMessage(TEXT.MODALITY_QUARTERLY_TITLE),
        content: formatMessage(TEXT.MODALITY_QUARTERLY_DESC),
      },
      {
        id: MODALITY.ON_DEMAND,
        title: formatMessage(TEXT.MODALITY_ONDEMAND_TITLE),
        content: formatMessage(TEXT.MODALITY_ONDEMAND_DESC),
      },
    ],
    [control, formatMessage, notAssigned, v1],
  );

  const optionsList = useMemo(() => {
    return options
      .filter(({id}) => !hiddenOptions.includes(id))
      .map(({content, ...rest}) => (hideDescription ? rest : {content, ...rest}));
  }, [options, hiddenOptions, hideDescription]);

  return (
    <OptionSelector
      options={optionsList}
      selected={modality}
      onSelected={(id: string) => onModalityChange(id as Modality)}
      hideDescriptionOnBlur={hideDescriptionOnBlur}
    />
  );
};

export {FrequencySelector};
